import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { getAppBaseUrl } from "../utils/url";
import {
  Users,
  Search,
  Plus,
  Shield,
  User,
  Baby,
  Copy,
  Trash2,
  X,
  Mail,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Clock,
  Link2,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type DbRole = "owner" | "coach" | "assistant" | "player" | "parent";
type DisplayRole = "coach" | "player" | "parent";
type TabKey = "all" | DisplayRole;

type Member = {
  id: string;
  user_id: string | null;
  role: DbRole;
  status: string;
  invited_email: string | null;
  invite_token: string | null;
  player_number: number | null;
  display_name: string;
  email: string | null;
};

type ParentLink = {
  id: string;
  player_member_id: string;
  parent_member_id: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function dbRoleToDisplay(role: DbRole): DisplayRole {
  if (role === "player") return "player";
  if (role === "parent") return "parent";
  return "coach";
}

function roleLabel(role: DbRole) {
  switch (role) {
    case "owner": return "Coach (propriétaire)";
    case "coach": return "Coach";
    case "assistant": return "Assistant";
    case "player": return "Joueur";
    case "parent": return "Parent";
  }
}

function roleBadgeClass(role: DbRole) {
  if (role === "player") return "bg-blue-100 text-blue-900 dark:bg-blue-900/20 dark:text-blue-200";
  if (role === "parent") return "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200";
  return "bg-purple-100 text-purple-900 dark:bg-purple-900/20 dark:text-purple-200";
}

const CAN_MANAGE_TEAM_ROLES: DbRole[] = ["owner", "coach", "assistant"];
const CAN_MANAGE_CLUB_ROLES = ["dirigeant", "cadre_technique"];

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function TeamMembers() {
  const { clubId, teamId } = useParams<{ clubId: string; teamId: string }>();

  // ── Data ──
  const [members, setMembers] = useState<Member[]>([]);
  const [teamName, setTeamName] = useState("");
  const [teamRole, setTeamRole] = useState<DbRole | null>(null);
  const [clubRole, setClubRole] = useState<string | null>(null);
  const [playersCanSeeMembers, setPlayersCanSeeMembers] = useState(true);
  const [parentLinks, setParentLinks] = useState<ParentLink[]>([]);

  // ── UI ──
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<TabKey>("all");
  const [q, setQ] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);

  // ── Invite modal ──
  const [openInvite, setOpenInvite] = useState(false);
  const [inviteRole, setInviteRole] = useState<DbRole>("player");
  const [inviteEmail, setInviteEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [pendingLink, setPendingLink] = useState("");
  const [copied, setCopied] = useState(false);

  // ── Parent-player link modal ──
  const [linkModalPlayer, setLinkModalPlayer] = useState<Member | null>(null);
  const [linkSelection, setLinkSelection] = useState<Set<string>>(new Set());
  const [savingLinks, setSavingLinks] = useState(false);
  const [linkError, setLinkError] = useState("");

  const canManage =
    (!!teamRole && CAN_MANAGE_TEAM_ROLES.includes(teamRole)) ||
    (!!clubRole && CAN_MANAGE_CLUB_ROLES.includes(clubRole));

  const canViewMembers = canManage || playersCanSeeMembers;

  // ── Load ──
  const load = useCallback(async () => {
    if (!teamId || !clubId) return;

    try {
      setLoading(true);
      setError("");

      const { data: { user }, error: authErr } = await supabase.auth.getUser();
      if (authErr) throw authErr;
      if (!user) throw new Error("Utilisateur non connecté.");

      const [teamRes, membersRes, teamRoleRes, clubRoleRes, settingsRes] = await Promise.all([
        supabase.from("teams").select("team_name").eq("id", teamId).maybeSingle(),
        supabase
          .from("team_members")
          .select("id, user_id, role, status, invited_email, invite_token, player_number")
          .eq("team_id", teamId)
          .in("status", ["active", "invited"])
          .order("role", { ascending: true }),
        supabase
          .from("team_members")
          .select("role")
          .eq("team_id", teamId)
          .eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("club_members")
          .select("role")
          .eq("club_id", clubId)
          .eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("team_settings")
          .select("players_can_see_members")
          .eq("team_id", teamId)
          .maybeSingle(),
      ]);

      if (membersRes.error) throw membersRes.error;

      setTeamName(teamRes.data?.team_name?.trim() || "Équipe");
      setTeamRole((teamRoleRes.data?.role as DbRole) ?? null);
      setClubRole(clubRoleRes.data?.role ?? null);
      setPlayersCanSeeMembers(settingsRes.data?.players_can_see_members ?? true);

      const rows = membersRes.data || [];

      // Batch-fetch display names from user_profiles
      const userIds = rows.map((r: any) => r.user_id).filter(Boolean) as string[];
      let profileMap = new Map<string, string>();

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("user_profiles")
          .select("id, full_name, pseudo, public_use_pseudo")
          .in("id", userIds);

        for (const p of profiles || []) {
          const name =
            p.public_use_pseudo && p.pseudo
              ? p.pseudo
              : p.full_name?.trim() || "";
          profileMap.set(p.id, name);
        }
      }

      const resolved: Member[] = rows.map((r: any) => {
        const profileName = r.user_id ? profileMap.get(r.user_id) || "" : "";
        const display_name =
          profileName || r.invited_email?.split("@")[0] || "Membre";
        return {
          id: r.id,
          user_id: r.user_id,
          role: r.role as DbRole,
          status: r.status,
          invited_email: r.invited_email,
          invite_token: r.invite_token,
          player_number: r.player_number,
          display_name,
          email: r.invited_email ?? null,
        };
      });

      setMembers(resolved);

      // Parent ↔ player links (table may not exist yet, ignore silently)
      const { data: linkRows, error: linkErr } = await supabase
        .from("team_player_parents")
        .select("id, player_member_id, parent_member_id")
        .eq("team_id", teamId);
      setParentLinks(linkErr ? [] : (linkRows as ParentLink[]) || []);
    } catch (err: any) {
      setError(err?.message || "Impossible de charger les membres.");
    } finally {
      setLoading(false);
    }
  }, [teamId, clubId]);

  useEffect(() => { load(); }, [load]);

  // ── Invite submit ──
  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!teamId) return;

    setSubmitting(true);
    setSubmitError("");

    try {
      const token = crypto.randomUUID();
      const { error: insertErr } = await supabase.from("team_members").insert({
        team_id: teamId,
        invited_email: inviteEmail.trim() || null,
        status: "invited",
        role: inviteRole,
        invite_token: token,
      });

      if (insertErr) throw insertErr;

      const link = `${getAppBaseUrl()}/accept-invite?token=${encodeURIComponent(token)}`;
      setPendingLink(link);
      setInviteEmail("");
      await load();
    } catch (err: any) {
      setSubmitError(err?.message || "Erreur lors de l'invitation.");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Copy invite link ──
  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      alert(link);
    }
  }

  // ── Revoke member ──
  async function handleRevoke(memberId: string) {
    if (!window.confirm("Retirer ce membre de l'équipe ?")) return;
    setRemoving(memberId);
    const { error: revErr } = await supabase
      .from("team_members")
      .update({ status: "revoked" })
      .eq("id", memberId);
    setRemoving(null);
    if (revErr) { alert(revErr.message); return; }
    setMembers((prev) => prev.filter((m) => m.id !== memberId));
  }

  function resetInvite() {
    setInviteRole("player");
    setInviteEmail("");
    setSubmitError("");
    setPendingLink("");
    setCopied(false);
  }

  // ── Filter ──
  const filtered = useMemo(() => {
    let base = members;
    if (tab !== "all") {
      base = members.filter((m) => dbRoleToDisplay(m.role) === tab);
    }
    const qq = q.trim().toLowerCase();
    if (!qq) return base;
    return base.filter((m) =>
      [m.display_name, m.email ?? "", m.player_number != null ? String(m.player_number) : ""]
        .join(" ")
        .toLowerCase()
        .includes(qq)
    );
  }, [members, tab, q]);

  // ── Counts for tabs ──
  const counts = useMemo(() => ({
    all: members.length,
    coach: members.filter((m) => dbRoleToDisplay(m.role) === "coach").length,
    player: members.filter((m) => m.role === "player").length,
    parent: members.filter((m) => m.role === "parent").length,
  }), [members]);

  // ── Parent ↔ player link maps ──
  const parentsByPlayer = useMemo(() => {
    const map = new Map<string, Member[]>();
    for (const link of parentLinks) {
      const parent = members.find((m) => m.id === link.parent_member_id);
      if (!parent) continue;
      const arr = map.get(link.player_member_id) ?? [];
      arr.push(parent);
      map.set(link.player_member_id, arr);
    }
    return map;
  }, [parentLinks, members]);

  const childrenByParent = useMemo(() => {
    const map = new Map<string, Member[]>();
    for (const link of parentLinks) {
      const player = members.find((m) => m.id === link.player_member_id);
      if (!player) continue;
      const arr = map.get(link.parent_member_id) ?? [];
      arr.push(player);
      map.set(link.parent_member_id, arr);
    }
    return map;
  }, [parentLinks, members]);

  const availableParents = useMemo(
    () => members.filter((m) => m.role === "parent"),
    [members]
  );

  // ── Link modal handlers ──
  function openLinkModal(player: Member) {
    setLinkModalPlayer(player);
    setLinkSelection(new Set((parentsByPlayer.get(player.id) ?? []).map((p) => p.id)));
    setLinkError("");
  }

  function toggleParentSelection(parentId: string) {
    setLinkSelection((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  }

  async function saveLinks() {
    if (!linkModalPlayer || !teamId) return;
    setSavingLinks(true);
    setLinkError("");

    const current = parentLinks.filter((l) => l.player_member_id === linkModalPlayer.id);
    const currentIds = new Set(current.map((l) => l.parent_member_id));

    const toRemove = current.filter((l) => !linkSelection.has(l.parent_member_id));
    const toAdd = [...linkSelection].filter((id) => !currentIds.has(id));

    try {
      if (toRemove.length > 0) {
        const { error: delErr } = await supabase
          .from("team_player_parents")
          .delete()
          .in("id", toRemove.map((l) => l.id));
        if (delErr) throw delErr;
      }
      if (toAdd.length > 0) {
        const { error: insErr } = await supabase.from("team_player_parents").insert(
          toAdd.map((parentId) => ({
            team_id: teamId,
            player_member_id: linkModalPlayer.id,
            parent_member_id: parentId,
          }))
        );
        if (insErr) throw insErr;
      }
      await load();
      setLinkModalPlayer(null);
    } catch (err: any) {
      const msg: string = err?.message || "";
      setLinkError(
        msg.includes("does not exist") || msg.includes("relation") || msg.includes("schema cache")
          ? "La table de liaison parent-joueur n'existe pas encore. Exécutez le script SQL fourni pour l'activer."
          : msg || "Erreur lors de la mise à jour des liens."
      );
    } finally {
      setSavingLinks(false);
    }
  }

  // ── Guards ──
  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
        <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement des membres…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300">
        <div className="inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      </div>
    );
  }

  if (!canViewMembers) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
        <Users className="h-8 w-8 text-gray-400" />
        <div className="text-sm text-center">
          La liste des membres n'est pas accessible : cette option a été désactivée par le staff de l'équipe.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col items-center text-center gap-3 sm:flex-row sm:items-center sm:justify-between sm:text-left">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Membres équipe</div>
          <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {teamName ? `${teamName} • Membres` : "Membres"}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {members.length} membre{members.length !== 1 ? "s" : ""} — coachs, joueurs et parents.
          </div>
        </div>

        {canManage && (
          <button
            type="button"
            onClick={() => setOpenInvite(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm"
          >
            <Plus className="h-4 w-4" />
            Inviter
          </button>
        )}
      </div>

      {!canManage && (
        <div className="rounded-2xl bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-900/40 p-4 text-sm text-yellow-900 dark:text-yellow-200">
          Tu peux consulter les membres, mais seuls les coachs et dirigeants peuvent inviter ou retirer.
        </div>
      )}

      {/* Controls */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {(
            [
              { key: "all" as const,    label: "Tous",       icon: <Users className="h-4 w-4" />,  count: counts.all },
              { key: "coach" as const,  label: "Coachs",     icon: <Shield className="h-4 w-4" />, count: counts.coach },
              { key: "player" as const, label: "Joueurs",    icon: <User className="h-4 w-4" />,   count: counts.player },
              { key: "parent" as const, label: "Parents",    icon: <Baby className="h-4 w-4" />,   count: counts.parent },
            ]
          ).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cx(
                "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm border transition",
                tab === t.key
                  ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                  : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
              )}
            >
              <span className="text-gray-600 dark:text-gray-300">{t.icon}</span>
              {t.label}
              <span className="ml-0.5 text-xs text-gray-400 dark:text-gray-500">({t.count})</span>
            </button>
          ))}
        </div>

        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
            placeholder="Rechercher (nom, email, numéro…)"
          />
        </div>
      </div>

      {/* Member list */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500">
            <Users className="h-8 w-8" />
            <div className="text-sm">Aucun membre trouvé.</div>
          </div>
        ) : (
          filtered.map((m) => (
            <div key={m.id} className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {m.display_name}
                      {m.role === "player" && m.player_number != null
                        ? <span className="text-gray-500 dark:text-gray-400 font-normal"> • #{m.player_number}</span>
                        : null}
                    </div>

                    <span className={cx("text-xs px-2 py-0.5 rounded-full", roleBadgeClass(m.role))}>
                      {roleLabel(m.role)}
                    </span>

                    {m.status === "invited" && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
                        <Clock className="h-3 w-3" />
                        En attente
                      </span>
                    )}
                  </div>

                  {m.email && (
                    <div className="mt-1.5 inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <Mail className="h-3.5 w-3.5" />
                      <span className="truncate">{m.email}</span>
                    </div>
                  )}

                  {m.status === "invited" && m.invite_token && canManage && (
                    <button
                      type="button"
                      onClick={() => copyLink(`${getAppBaseUrl()}/accept-invite?token=${encodeURIComponent(m.invite_token!)}`)}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copier le lien d'invitation
                    </button>
                  )}

                  {/* Linked parents (on a player row) */}
                  {m.role === "player" && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {(parentsByPlayer.get(m.id) ?? []).map((p) => (
                        <span
                          key={p.id}
                          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300"
                        >
                          <Baby className="h-3 w-3" />
                          {p.display_name}
                        </span>
                      ))}
                      {canManage && (
                        <button
                          type="button"
                          onClick={() => openLinkModal(m)}
                          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-dashed border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition"
                        >
                          <Link2 className="h-3 w-3" />
                          Lier des parents
                        </button>
                      )}
                    </div>
                  )}

                  {/* Linked children (on a parent row) */}
                  {m.role === "parent" && (childrenByParent.get(m.id) ?? []).length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {(childrenByParent.get(m.id) ?? []).map((child) => (
                        <span
                          key={child.id}
                          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                        >
                          <User className="h-3 w-3" />
                          {child.display_name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {canManage && (
                  <button
                    type="button"
                    onClick={() => handleRevoke(m.id)}
                    disabled={removing === m.id}
                    className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition disabled:opacity-40"
                    aria-label="Retirer"
                  >
                    {removing === m.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Trash2 className="h-4 w-4" />}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Invite modal */}
      {openInvite && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setOpenInvite(false); resetInvite(); }} />
          <div className="absolute inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center p-3">
            <div className="w-full sm:max-w-lg rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="font-semibold text-gray-900 dark:text-gray-100">Inviter un membre</div>
                <button type="button" onClick={() => { setOpenInvite(false); resetInvite(); }}
                  className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:underline">
                  <X className="h-4 w-4" />Fermer
                </button>
              </div>

              {submitError && (
                <div className="mb-3 rounded-xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                  {submitError}
                </div>
              )}

              {/* Success: show invite link */}
              {pendingLink ? (
                <div className="space-y-3">
                  <div className="rounded-2xl border border-green-200 bg-green-50 dark:border-green-900/40 dark:bg-green-950/20 p-4">
                    <div className="flex items-center gap-2 text-sm text-green-800 dark:text-green-300 font-semibold mb-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Invitation créée
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 break-all mb-3 font-mono bg-white dark:bg-gray-900 rounded-xl p-2 border border-gray-200 dark:border-gray-800">
                      {pendingLink}
                    </div>
                    <button
                      type="button"
                      onClick={() => copyLink(pendingLink)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      {copied ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      {copied ? "Copié !" : "Copier le lien"}
                    </button>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => { setPendingLink(""); setCopied(false); }}
                      className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800">
                      Inviter quelqu'un d'autre
                    </button>
                    <button type="button" onClick={() => { setOpenInvite(false); resetInvite(); }}
                      className="px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm">
                      Fermer
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleInvite} className="space-y-3">
                  {/* Role selector */}
                  <div className="grid grid-cols-3 gap-2">
                    {(
                      [
                        { r: "coach" as const,  label: "Coach",  icon: <Shield className="h-4 w-4" /> },
                        { r: "player" as const, label: "Joueur", icon: <User className="h-4 w-4" /> },
                        { r: "parent" as const, label: "Parent", icon: <Baby className="h-4 w-4" /> },
                      ]
                    ).map((x) => (
                      <button
                        key={x.r}
                        type="button"
                        onClick={() => setInviteRole(x.r)}
                        className={cx(
                          "px-3 py-2 rounded-xl text-sm border inline-flex items-center justify-center gap-2",
                          inviteRole === x.r
                            ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                            : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                        )}
                      >
                        <span className="text-gray-500 dark:text-gray-400">{x.icon}</span>
                        {x.label}
                      </button>
                    ))}
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Email (optionnel)</label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="ex : joueur@exemple.fr"
                      className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
                    />
                    <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                      Un lien d'invitation sera généré. Vous pouvez l'envoyer manuellement.
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-2">
                    <button type="button" onClick={() => { setOpenInvite(false); resetInvite(); }}
                      className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800">
                      Annuler
                    </button>
                    <button type="submit" disabled={submitting}
                      className={cx(
                        "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm",
                        "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                        submitting && "opacity-60 cursor-not-allowed"
                      )}>
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      {submitting ? "Création…" : "Générer le lien"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Link parents modal */}
      {linkModalPlayer && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setLinkModalPlayer(null)} />
          <div className="absolute inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center p-3">
            <div className="w-full sm:max-w-lg rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="font-semibold text-gray-900 dark:text-gray-100">
                  Lier des parents à {linkModalPlayer.display_name}
                </div>
                <button type="button" onClick={() => setLinkModalPlayer(null)}
                  className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:underline">
                  <X className="h-4 w-4" />Fermer
                </button>
              </div>

              {linkError && (
                <div className="mb-3 rounded-xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                  {linkError}
                </div>
              )}

              {availableParents.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Aucun parent dans cette équipe pour l'instant. Invite d'abord un parent depuis le bouton "Inviter".
                </div>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-auto">
                  {availableParents.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <input
                        type="checkbox"
                        checked={linkSelection.has(p.id)}
                        onChange={() => toggleParentSelection(p.id)}
                        className="rounded border-gray-300 text-gray-900 focus:ring-gray-400"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {p.display_name}
                        </div>
                        {p.email && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{p.email}</div>
                        )}
                      </div>
                      {p.status === "invited" && (
                        <span className="shrink-0 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
                          <Clock className="h-3 w-3" />
                          En attente
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-4">
                <button type="button" onClick={() => setLinkModalPlayer(null)}
                  className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800">
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={saveLinks}
                  disabled={savingLinks || availableParents.length === 0}
                  className={cx(
                    "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm",
                    "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                    (savingLinks || availableParents.length === 0) && "opacity-60 cursor-not-allowed"
                  )}
                >
                  {savingLinks ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                  {savingLinks ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
